import { useTranslation } from 'react-i18next';
import type { ShipmentStatus, StopResponse } from '../../api/types';
import { shipmentStatusLabel } from './shipmentStatusLabel';
import { formatIst } from './delayReason';
import { StoppedPill } from './StoppedPill';
import { stopReasonLabel } from './stopLabels';
import styles from './ShipmentMilestoneStepper.module.css';

interface Props {
  status: ShipmentStatus;
  /** The open stop (dnm-work#165): the stage stays current and an amber Stopped pill is added. */
  openStop?: StopResponse | null;
}

interface Step {
  key: ShipmentStatus;
  label: string;
}

const LIFECYCLE_KEYS: ShipmentStatus[] = [
  'POSTED',
  'AWARDED',
  'ASSIGNED',
  'EN_ROUTE_PICKUP',
  'AT_PICKUP',
  'LOADED',
  'IN_TRANSIT',
  'AT_DROP',
  'DELIVERED',
  'COMPLETED',
];

const LIFECYCLE_STEPS: Step[] = LIFECYCLE_KEYS.map((key) => ({
  key,
  label: shipmentStatusLabel(key),
}));

export function ShipmentMilestoneStepper({ status, openStop }: Props) {
  const { t } = useTranslation();
  // Determine index of current status in the standard progression
  let activeIndex = LIFECYCLE_STEPS.findIndex((s) => s.key === status);
  // EXCEPTION is a retired status (dnm-work#165 C6): old shipments may still show it; no step is current.
  const isException = status === 'EXCEPTION';
  const isCancelled = status === 'CANCELLED';

  if (status === 'DRAFT') {
    activeIndex = -1;
  }

  return (
    <div className={styles.stepperCard}>
      <div className={styles.headerRow}>
        <div className={styles.title}>
          <span>{t('shipments.shipmentMilestoneStepper.title')}</span>
        </div>
        <div className={styles.statusTag}>
          {t('shipments.shipmentMilestoneStepper.current')} <strong title={status}>{shipmentStatusLabel(status)}</strong>
        </div>
      </div>

      <div className={styles.stepperContainer}>
        {LIFECYCLE_STEPS.map((step, idx) => {
          const isCompleted = activeIndex > idx || status === 'COMPLETED';
          const isCurrent = activeIndex === idx && !isException && !isCancelled;

          return (
            <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start', flex: idx < LIFECYCLE_STEPS.length - 1 ? 1 : 'none' }}>
              <div className={styles.stepItem}>
                <div
                  className={`${styles.circle} ${
                    isCompleted ? styles.circleCompleted : isCurrent ? styles.circleCurrent : ''
                  }`}
                >
                  {isCompleted ? '✓' : idx + 1}
                </div>
                <span
                  className={`${styles.label} ${
                    isCompleted ? styles.labelCompleted : isCurrent ? styles.labelCurrent : ''
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {idx < LIFECYCLE_STEPS.length - 1 && (
                <div
                  className={`${styles.connector} ${
                    activeIndex > idx || status === 'COMPLETED' ? styles.connectorCompleted : ''
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {openStop && (
        <div className={styles.stopRow}>
          <StoppedPill />
          <span className={styles.stopReason}>{stopReasonLabel(t, openStop.reason)}</span>
          <span className={styles.stopMeta}>
            {t('stops.stepper.since', { time: formatIst(openStop.startedAt) })}
          </span>
          <span className={styles.stopMeta}>
            {openStop.expectedResumeAt
              ? t('stops.stepper.expected', { time: formatIst(openStop.expectedResumeAt) })
              : t('stops.stepper.noExpected')}
          </span>
        </div>
      )}
    </div>
  );
}
